import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import {
  Button,
  DialogPhasePanel,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  DialogShellLayout,
  ResizableDialog,
  ResizableDialogContent
} from '@bliks-io/ui';

export interface TypePickerFooterAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface TypePickerModalShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sizes: Record<string, { width: number; minHeight: number }>;
  size: string;
  headerIcon: ReactNode;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  /** Left nav; the layout renders sidebar-less (chooser phase) when absent. */
  sidebar?: ReactNode;
  resizableAutoSaveId: string;
  /** Drives the crossfade between panes — one key per nav type (or phase). */
  phaseKey: string;
  footerHint?: string;
  /** Renders the ghost "back to suggestions" footer button when set. */
  onBackToBuckets?: () => void;
  primary?: TypePickerFooterAction;
  onEscapeKeyDown?: (event: globalThis.KeyboardEvent) => void;
  children: ReactNode;
}

/**
 * Chrome of a master-detail entity-picker modal: resizable dialog, header,
 * optional left type nav, crossfading content pane, and the cancel/save
 * footer with the "back to suggestions" affordance. Extracted from the
 * successor picker; consumed by the successor and helpers modals.
 */
export function TypePickerModalShell({
  open,
  onOpenChange,
  sizes,
  size,
  headerIcon,
  eyebrow,
  title,
  subtitle,
  sidebar,
  resizableAutoSaveId,
  phaseKey,
  footerHint,
  onBackToBuckets,
  primary,
  onEscapeKeyDown,
  children
}: TypePickerModalShellProps) {
  const { t } = useTranslation();

  return (
    <ResizableDialog open={open} onOpenChange={onOpenChange}>
      <ResizableDialogContent
        sizes={sizes}
        size={size}
        hideAutoClose
        className="rounded-2xl bg-white p-0 gap-0 text-foreground shadow-2xl"
        onEscapeKeyDown={onEscapeKeyDown}
      >
        <DialogShellHeader
          icon={headerIcon}
          iconTone="green"
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          onClose={() => onOpenChange(false)}
        />

        <DialogShellBody padding="px-6 pt-5 pb-4">
          <DialogShellLayout
            sidebar={sidebar}
            sidebarClassName={sidebar ? 'bg-muted/40 rounded-xl p-3' : undefined}
            resizable={!!sidebar}
            resizableAutoSaveId={resizableAutoSaveId}
          >
            <DialogPhasePanel phaseKey={phaseKey} className="flex-1 min-w-0">
              {children}
            </DialogPhasePanel>
          </DialogShellLayout>
        </DialogShellBody>

        <DialogShellFooter
          hint={footerHint}
          leftSlot={
            onBackToBuckets ? (
              <Button type="button" variant="ghost" wrap onClick={onBackToBuckets}>
                <ArrowLeft />
                {t('entityPicker.common.switchToBucketsLabel')}
              </Button>
            ) : undefined
          }
          secondary={{
            label: t('common.cancel'),
            onClick: () => onOpenChange(false)
          }}
          primary={primary}
        />
      </ResizableDialogContent>
    </ResizableDialog>
  );
}
